import { ICode } from '@/types/ICode';
import { IModifierRead } from '@/types/IModifierRead';
import { IParsedCase } from '@/types/IParsedCase';
import { EAudioRecordingType } from '@/types/audio/EAudioRecordingType';
import { isDevelopment, isTest } from '@/utils/env';
import { datadogRum } from '@datadog/browser-rum';
import mixpanel from 'mixpanel-browser';

export function fireMixpanelEvent(name: string, params?: object) {
  if (params) {
    mixpanel.track(name, params);
  } else {
    mixpanel.track(name);
  }
}

export function fireEvent(name: string, params?: object) {
  if (!checkPrerequisites()) {
    if (params) {
      console.log('Event: ' + name, params);
    } else {
      console.log('Event: ' + name);
    }
    return;
  }
  // Never let a telemetry failure propagate into application code (e.g. case intake).
  try {
    datadogRum.addAction(name, params);
  } catch (err) {
    console.error('Failed to send analytics event', name, err);
  }
}

export function casePredictionsRequested(caseId: number) {
  fireEvent('Case Predictions Requested', getAnalyticsCase(caseId));
}

export function codeRemoved(caseId: number, code: ICode) {
  let caseObj = getAnalyticsCase(caseId);
  fireEvent('Code Removed', { ...caseObj, code });
}

export function codeAdded(caseId: number, code: ICode) {
  let caseObj = getAnalyticsCase(caseId);
  fireEvent('Code Added', { ...caseObj, code });
}

export function codeSearched(caseId: number, query: string) {
  let caseObj = getAnalyticsCase(caseId);
  fireEvent('Code Searched', { ...caseObj, query });
}

export function codeModifierSearched(caseId: number, code: string, query: string) {
  let caseObj = getAnalyticsCase(caseId);
  fireEvent('Code Modifier Searched', { ...caseObj, code, query });
}

export function codeModifiersChanged(caseId: number, code: ICode, modifiers: IModifierRead[]) {
  let caseObj = getAnalyticsCase(caseId);
  fireEvent('Code Modifiers Changed', {
    ...caseObj,
    code: code.name,
    internalCodeId: code._internalId,
    modifiers,
  });
}

export function codeSearchResultAdded(caseId: number, query: string, code: ICode) {
  let caseObj = getAnalyticsCase(caseId);
  fireEvent('Code Search Result Added', {
    ...caseObj,
    query,
    code,
  });
}

export function caseSubmittedAndUpdated(
  caseId: number,
  audioLength: number,
  processingTime: number,
  editMetrics: {
    originalNoteLength: number;
    submittedNoteLength: number;
    characterErrorRate: number;
    editDistance: number;
  },
) {
  fireEvent('Case Submitted and Updated', {
    ...getAnalyticsCase(caseId),
    audioLength,
    processingTime,
    ...editMetrics,
  });
}
export function caseSubmittedWithLLMAssistance(
  caseId: number,
  audioLength: number,
  processingTime: number,
  editMetrics: {
    originalNoteLength: number;
    submittedNoteLength: number;
    characterErrorRate: number;
    editDistance: number;
  },
  originalCER?: number,
  llmMode?: string,
) {
  fireEvent('Case Submitted with LLM Assistance', {
    ...getAnalyticsCase(caseId),
    audioLength,
    processingTime,
    ...editMetrics,
    originalCER,
    llmMode,
  });
}

export function flaggedCaseSubmittedAndUpdated(caseId: number) {
  fireEvent('Flagged Case Submitted and Updated', getAnalyticsCase(caseId));
}

export function feedbackProvided(caseId: number, feedback: number, scale: 5) {
  fireEvent('Feedback Provided', { ...getAnalyticsCase(caseId), feedback, scale });
}

export function caseFlaggedAndUpdated(caseId: number, reason: string, confirmed_note: boolean) {
  let caseObj = getAnalyticsCase(caseId);
  fireEvent('Case Flagged and Updated', { ...caseObj, reason, confirmed_note });
}

export function trackCaseNoteUpdatedSilently(caseId: number) {
  fireEvent('Case note updated silently', getAnalyticsCase(caseId));
}

export function trackLLMFixerUsed(
  caseId: number,
  requestLatency: number,
  metrics: {
    originalNoteLength: number;
    submittedNoteLength: number;
    characterErrorRate: number;
    editDistance: number;
  },
) {
  fireEvent('LLM Fixer Used', { ...getAnalyticsCase(caseId), requestLatency, ...metrics });
}

function getAnalyticsCase(caseId: number) {
  return { caseId };
}

export function checkPrerequisites(): boolean {
  if (isDevelopment() || isTest()) {
    return false;
  }

  return true;
}

export function caseFullAudioStored(caseId: number) {
  fireEvent('Full audio stored', getAnalyticsCase(caseId));
}
export function caseSegmentAudioStored(caseId: number) {
  fireEvent('Segment audio stored', getAnalyticsCase(caseId));
}
export function caseFullAudioStoringRetry(caseId: number, retryAttempt: number) {
  fireEvent('Full audio storing retry', { ...getAnalyticsCase(caseId), retryAttempt });
}
export function caseSegmentAudioStoringRetry(caseId: number, retryAttempt: number) {
  fireEvent('Segment audio storing retry', { ...getAnalyticsCase(caseId), retryAttempt });
}
export const trackStartAudioProcessingAttempt = (
  caseId: number,
  audioType: EAudioRecordingType,
  isSelfHostedWhisper: boolean,
  attempt: number,
) => {
  if (audioType === EAudioRecordingType.FULL) {
    trackStoringFullAudioRequested(caseId, isSelfHostedWhisper, attempt);
  } else {
    trackStoringAndTranscribingSegmentAudioRequested(caseId, isSelfHostedWhisper, attempt);
  }
};

export function caseLocked(caseId: number) {
  fireEvent('Case locked', getAnalyticsCase(caseId));
}
export function caseUnlocked(caseId: number) {
  fireEvent('Case unlocked', getAnalyticsCase(caseId));
}

export function trackCaseReceivedFromEHR(parsedCase: IParsedCase) {
  fireEvent('Case received from EHR', { externalID: parsedCase.external_id });
}

export type ICaseDiagnosticsSource =
  | 'raw-request'
  | 'create-payload'
  | 'edit-payload'
  | 'parsed-case';

type ShapeNode =
  | { type: 'null' }
  | { type: 'undefined' }
  | { type: 'string'; length: number }
  | { type: 'number' }
  | { type: 'boolean' }
  | { type: 'other'; truncated?: boolean }
  | { type: 'array'; length: number; elementTypeCounts: Record<string, number> | null }
  | {
      type: 'object';
      /** All descendant key names, flattened across nested objects/arrays-of-objects. */
      keys: string[];
      /** Per-key value type for this object's direct keys. */
      field_types: Record<string, string>;
    };

/**
 * Recursively describe the *shape* of any unknown JSON/value. Always lists key
 * names and per-key value types at every object level (an unknown schema can't
 * be allow-listed; the keys themselves are safe — only the *values* could be
 * PII, so values are never emitted, only their types and lengths).
 *
 * This single function is the one way we describe an unknown payload, and is
 * shared by every intake diagnostics event so their output is consistent.
 */
const MAX_DESCRIBE_SHAPE_DEPTH = 10;

function describeShape(value: unknown, depth = 0): ShapeNode {
  if (depth > MAX_DESCRIBE_SHAPE_DEPTH) {
    return { type: 'other', truncated: true };
  }
  if (value === null) {
    return { type: 'null' };
  }
  if (value === undefined) {
    return { type: 'undefined' };
  }
  if (typeof value === 'string') {
    return { type: 'string', length: value.length };
  }
  if (typeof value === 'number') {
    return { type: 'number' };
  }
  if (typeof value === 'boolean') {
    return { type: 'boolean' };
  }
  if (Array.isArray(value)) {
    const elementTypeCounts: Record<string, number> = {};
    for (const item of value) {
      const t = describeShape(item, depth + 1).type;
      elementTypeCounts[t] = (elementTypeCounts[t] ?? 0) + 1;
    }
    return {
      type: 'array',
      length: value.length,
      elementTypeCounts: Object.keys(elementTypeCounts).length > 0 ? elementTypeCounts : null,
    };
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as object);
    // Null prototype so a raw object with an own `__proto__` key still records
    // its type as an own property instead of mutating the prototype chain.
    const fieldTypes: Record<string, string> = Object.create(null);
    const keys = new Set<string>();
    const collectObjectKeys = (sub: unknown, depth: number) => {
      const child = describeShape(sub, depth);
      if (child.type === 'object') {
        child.keys.forEach((k) => keys.add(k));
      }
    };
    for (const [key, sub] of entries) {
      keys.add(key);
      fieldTypes[key] =
        sub == null
          ? sub === null
            ? 'null'
            : 'undefined'
          : Array.isArray(sub)
            ? 'array'
            : typeof sub;
      if (Array.isArray(sub)) {
        // The array branch only reports element-type counts, so descend into
        // each element's object keys separately.
        sub.forEach((item) => collectObjectKeys(item, depth + 1));
      } else {
        collectObjectKeys(sub, depth + 1);
      }
    }
    return { type: 'object', keys: [...keys].sort(), field_types: fieldTypes };
  }
  return { type: 'other' };
}

/**
 * Telemetry for case intake must never break case intake: if describing an
 * unknown payload throws (e.g. a cyclic reference or a throwing accessor on
 * unknown EHR data), swallow it rather than returning partial data or letting
 * it propagate to the caller.
 */
function safeDescribe(value: unknown): { ok: false } | { ok: true; shape: ShapeNode } {
  try {
    return { ok: true, shape: describeShape(value) };
  } catch {
    return { ok: false };
  }
}

/**
 * Tag + emit a described shape for an intake diagnostics event.
 */
function emitShapeMeta(eventName: string, source: ICaseDiagnosticsSource, value: unknown): void {
  const result = safeDescribe(value);
  if (!result.ok) {
    fireEvent(eventName, { source, error: true });
    return;
  }
  fireEvent(eventName, { source, shape: result.shape });
}

export function trackRawEHRRequestMeta(request: any) {
  emitShapeMeta('Case received from EHR - request meta', 'raw-request', request);
}

export function trackCasePayloadMeta(
  inputCase: Record<string, unknown>,
  source: 'create-payload' | 'edit-payload',
) {
  try {
    const result = safeDescribe(inputCase);
    if (!result.ok) {
      fireEvent('Case payload meta', { source, error: true });
      return;
    }
    fireEvent('Case payload meta', {
      source,
      shape: result.shape,
      has_note: Boolean(inputCase?.note),
      note_length: typeof inputCase?.note === 'string' ? inputCase.note.length : null,
      external_id_present: Boolean(inputCase?.external_id),
      status_present: inputCase?.status != null,
    });
  } catch (e) {
    console.error('Failed to describe case payload meta', e);
  }
}

/**
 * Track a backend API error to Datadog. The Axios interceptor in BackendAPI.ts
 * builds a `safeError` with headers, request body, and query params stripped.
 * For 422s the FastAPI `input` field (which echoes rejected values) is also
 * stripped.
 *
 * All errors are reported via `datadogRum.addError` with status, method, URL,
 * and for 422s the validation detail (loc, msg, type). No raw response bodies
 * are sent.
 */
export function trackBackendError(error: any) {
  const context: Record<string, any> = {
    status: error?.response?.status,
    method: error?.method,
    url: error?.url,
  };

  if (error?.response?.status === 422) {
    const data = error?.response?.data;
    const detail = Array.isArray(data?.detail) ? data.detail : data?.detail ? [data.detail] : [];
    // Only loc + msg + type are safe: the FastAPI `input` field echoes the rejected value (PII).
    context.details = detail.map((d: any) => ({
      loc: d?.loc,
      msg: d?.msg,
      type: d?.type,
    }));
  }

  if (checkPrerequisites()) {
    try {
      datadogRum.addError(error?.message ?? error, context);
    } catch (err) {
      console.error('Failed to report backend error to Datadog', err);
    }
  } else {
    console.error('Backend error:', error?.message ?? error, context);
  }
}

export function trackStartRecording(microphoneName: string) {
  fireEvent('Recording started', { microphoneName });
}
export function trackStartSegmentRecording() {
  fireEvent('Segment recording started');
}
export function trackStopFullRecording() {
  fireEvent('Full recording stopped');
}
export function trackStopSegmentRecording() {
  fireEvent('Segment recording stopped');
}

export function trackTranscriptionStarted(metadata: { sessionId: string }) {
  fireEvent('Transcription started', metadata);
}

export function trackTranscriptionReconnectAttempt(retryAttempt: number) {
  fireEvent('Transcription websocket reconnect attempt', { retryAttempt });
}

export function trackTranscriptionStopped(metadata: { sessionId: string }) {
  fireEvent('Transcription stopped', metadata);
}

export function trackPauseResumeFullRecording(isPaused: boolean) {
  if (isPaused) {
    fireEvent('Full recording paused');
  } else {
    fireEvent('Full recording resumed');
  }
}
export function trackPauseResumeSegmentRecording(isPaused: boolean) {
  if (isPaused) {
    fireEvent('Segment recording paused');
  } else {
    fireEvent('Segment recording resumed');
  }
}

export function trackPlayAudio(caseId: number) {
  fireEvent('Audio playing', getAnalyticsCase(caseId));
}

export function trackPauseAudio(caseId: number) {
  fireEvent('Audio paused', getAnalyticsCase(caseId));
}

export function trackEnableSilenceCut(caseId: number) {
  fireEvent('Silence cut enabled', getAnalyticsCase(caseId));
}

export function trackDisableSilenceCut(caseId: number) {
  fireEvent('Silence cut disabled', getAnalyticsCase(caseId));
}

export function trackPlaybackSpeedChange(caseId: number, speed: number) {
  let caseObj = getAnalyticsCase(caseId);
  fireEvent('Playback speed changed', { ...caseObj, speed: speed });
}

export function trackStoringAndTranscribingSegmentAudioRequested(
  caseId: number,
  isSelfHostedWhisper: boolean,
  attemptNumber: number,
) {
  const caseObj = getAnalyticsCase(caseId);
  fireEvent('Segment audio storing and transcribing requested', {
    ...caseObj,
    isSelfHostedWhisper,
    attemptNumber,
  });
}
export function trackStoringFullAudioRequested(
  caseId: number,
  isSelfHostedWhisper: boolean, // doesn't make much sense for simple storing of the full audio; kept for easier abstraction.
  attemptNumber: number,
) {
  const caseObj = getAnalyticsCase(caseId);
  fireEvent('Full audio storing requested', { ...caseObj, isSelfHostedWhisper, attemptNumber });
}

export function trackComingBackOnline() {
  fireEvent('The app has come back online.');
}

export function trackTokenUpdatedAfterComingBackOnline() {
  fireEvent('The auth token has been updated after coming back online.');
}

export function trackTokenUpdatedOnReceivingNewCase() {
  fireEvent('The auth token has been updated on receiving a new case.');
}
